import { useCallback, useEffect, useRef, useState } from "react"

import type { StorageScope } from "@/utils/scopedStorageKey"

import { useOptionalWorkspaceContext } from "../contexts/WorkspaceContext"
import { useScopedStorageKey } from "./useScopedStorageKey"

function readValue<T>(key: string | undefined, fallback: T): T {
  if (typeof window === "undefined" || !key) {
    return fallback
  }
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function writeValue<T>(key: string | undefined, value: T): void {
  if (typeof window === "undefined" || !key) {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Ignore storage failures (private mode / quota); value still lives in
    // memory for the rest of the session.
  }
}

type ScopedValueUpdate<T> = T | ((previous: T) => T)

function applyUpdate<T>(update: ScopedValueUpdate<T>, previous: T): T {
  return typeof update === "function"
    ? (update as (previous: T) => T)(previous)
    : update
}

/**
 * Like `useLocalStorage`, but the key is scoped by org (and, by default,
 * workspace) via `useScopedStorageKey` — use this instead of `useLocalStorage`
 * for any org- or workspace-specific cache. The first render always uses
 * `initialValue`, matching the server render; the stored value hydrates after
 * mount and again whenever the active org/workspace changes, so a client-side
 * workspace switch can't leave state hydrated under the previous scope.
 * Writes go to the scope of the current render. Until the organization and
 * the route's workspace resolve, changes stay in memory; update functions
 * then replay on top of the stored value, while a whole replacement value
 * gives way to it. A `null` base keeps the value in memory only, never
 * reading or writing storage.
 */
export function useScopedLocalStorage<T>(
  base: string | null,
  initialValue: T,
  scope?: StorageScope,
): [T, (update: ScopedValueUpdate<T>) => void] {
  const resolvedKey = useScopedStorageKey(base ?? "", scope)
  const workspaceContext = useOptionalWorkspaceContext()
  // While the route's workspace is still loading, the organization-level
  // fallback key is not this scope: treat it as unresolved so nothing is read
  // from or written to it.
  const isWorkspacePending =
    scope !== "organization" &&
    workspaceContext != null &&
    (workspaceContext.isWorkspaceCatalogLoading ||
      (workspaceContext.routeWorkspaceId != null &&
        workspaceContext.currentWorkspace == null))
  const scopedKey =
    base === null || isWorkspacePending ? undefined : resolvedKey
  // The scope `value` was loaded for. While it trails `scopedKey` (between a
  // scope change and the hydration effect) the hook shows `initialValue`
  // rather than the previous scope's value.
  const [state, setState] = useState<{ key: string | undefined; value: T }>({
    key: undefined,
    value: initialValue,
  })
  const initializedForKeyRef = useRef<string | undefined>(undefined)
  const initialValueRef = useRef(initialValue)
  initialValueRef.current = initialValue
  // Update functions applied before the scope resolves. Once it does, they
  // replay on top of that scope's stored value, so they neither get lost nor
  // replace it.
  const pendingRef = useRef<ScopedValueUpdate<T>[]>([])

  useEffect(() => {
    if (initializedForKeyRef.current === scopedKey) {
      return
    }
    initializedForKeyRef.current = scopedKey
    const pending = pendingRef.current
    if (pending.length > 0 && scopedKey) {
      pendingRef.current = []
      const next = pending.reduce<T>(
        (value, update) => applyUpdate(update, value),
        readValue(scopedKey, initialValueRef.current),
      )
      writeValue(scopedKey, next)
      setState({ key: scopedKey, value: next })
      return
    }
    setState({
      key: scopedKey,
      value: readValue(scopedKey, initialValueRef.current),
    })
  }, [scopedKey])

  const value = state.key === scopedKey ? state.value : initialValue

  const setValue = useCallback(
    (update: ScopedValueUpdate<T>) => {
      // An update before hydration builds on the destination scope's stored
      // value, never on another scope's.
      const previous =
        state.key === scopedKey
          ? state.value
          : readValue(scopedKey, initialValueRef.current)
      const next = applyUpdate(update, previous)
      setState({ key: scopedKey, value: next })
      if (scopedKey) {
        writeValue(scopedKey, next)
      } else if (base !== null && typeof update === "function") {
        pendingRef.current = [...pendingRef.current, update]
      } else {
        // A whole replacement value was built without the stored value, so it
        // stays in memory and the stored value wins once the scope resolves.
        pendingRef.current = []
      }
    },
    [base, scopedKey, state],
  )

  return [value, setValue]
}
